import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Smartphone, Play, XCircle, Clock } from 'lucide-react';
import type { Profile, Membership, MembershipPlan } from './types';

// Import card images
import cardBronce from '@/assets/membership-card-bronce.png';
import cardPlata from '@/assets/membership-card-plata.png';
import cardOro from '@/assets/membership-card-oro.png';

const planCardImages: Record<MembershipPlan, string> = {
  bronce: cardBronce,
  plata: cardPlata,
  oro: cardOro,
};

const planGlowColors: Record<MembershipPlan, string> = {
  bronce: 'rgba(180, 83, 9, 0.4)',
  plata: 'rgba(148, 163, 184, 0.4)',
  oro: 'rgba(234, 179, 8, 0.5)',
};

interface MembershipCardProps {
  profile: Profile;
  membership: Membership;
  isAdmin?: boolean;
  onStatusToggle?: () => void;
  isPending?: boolean; // New prop for pending state
  canToggleStatus?: boolean; // Permission to toggle status
}

export const MembershipCard: React.FC<MembershipCardProps> = ({
  profile,
  membership,
  isAdmin = false,
  onStatusToggle,
  isPending = false,
  canToggleStatus = true,
}) => {
  const [rotateX, setRotateX] = useState(0);
  const [rotateY, setRotateY] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Determine if card should show as pending (not activated)
  const showAsPending = isPending || (!membership.is_active && !membership.activated_at);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    
    // Calculate rotation (max 15 degrees)
    const rotateXValue = (mouseY / (rect.height / 2)) * -10;
    const rotateYValue = (mouseX / (rect.width / 2)) * 10;
    
    setRotateX(rotateXValue);
    setRotateY(rotateYValue);
  };

  const handleMouseLeave = () => {
    setRotateX(0);
    setRotateY(0);
    setIsHovered(false);
  };

  return (
    <motion.div 
      className={`relative w-full ${isAdmin ? 'max-w-sm sm:max-w-md mx-auto lg:mx-0' : ''} cursor-pointer`}
      style={{ perspective: '1000px' }}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
    >
      <motion.div
        className={`relative ${showAsPending ? 'grayscale opacity-70' : ''}`}
        animate={{
          rotateX,
          rotateY,
          scale: isHovered ? 1.02 : 1,
        }}
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20,
        }}
        style={{
          transformStyle: 'preserve-3d',
          boxShadow: isHovered && !showAsPending
            ? `0 20px 40px -15px rgba(0,0,0,0.5)`
            : '0 10px 30px -10px rgba(0,0,0,0.3)',
          borderRadius: '1rem',
        }}
      >
        {/* Card Image */}
        <img 
          src={planCardImages[membership.plan]} 
          alt={`Membresía ${membership.plan}`}
          className="w-full h-auto rounded-2xl bg-zinc-800"
          loading="eager"
          decoding="async"
          style={{ transform: 'translateZ(0)' }}
        />
        
        {/* Shine Effect Overlay */}
        <motion.div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            background: isHovered && !showAsPending
              ? `linear-gradient(${105 + rotateY * 2}deg, transparent 40%, rgba(255,255,255,0.15) 50%, transparent 60%)`
              : 'transparent',
            transform: 'translateZ(1px)',
          }}
        />
        
        {/* Status Badge Overlay */}
        <motion.div 
          className="absolute top-4 right-4"
          style={{ transform: 'translateZ(30px)' }}
        >
          {showAsPending ? (
            <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full bg-amber-500/30 text-amber-400 border border-amber-500/40 backdrop-blur-sm">
              <Clock className="w-3 h-3" />
              Pendiente
            </div>
          ) : isAdmin && onStatusToggle && canToggleStatus ? (
            <button
              onClick={onStatusToggle}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border backdrop-blur-sm transition-all hover:scale-105 ${
                membership.is_active
                  ? 'bg-green-500/30 text-green-400 border-green-500/40 hover:bg-green-500/40'
                  : 'bg-red-500/30 text-red-400 border-red-500/40 hover:bg-red-500/40'
              }`}
              title={membership.is_active ? 'Cancelar membresía' : 'Reactivar membresía'}
            >
              {membership.is_active ? (
                <>
                  <Play className="w-3 h-3" />
                  Activa
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3" />
                  Cancelada
                </>
              )}
            </button>
          ) : isAdmin && !canToggleStatus ? (
            <div className="flex items-center gap-1">
              {membership.is_active ? (
                <span className="bg-green-500/30 text-green-400 text-xs px-2 py-1 rounded-full border border-green-500/40 backdrop-blur-sm">
                  Activa
                </span>
              ) : (
                <span className="bg-red-500/30 text-red-400 text-xs px-2 py-1 rounded-full border border-red-500/40 backdrop-blur-sm">
                  Cancelada
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {membership.is_active ? (
                <span className="bg-green-500/30 text-green-400 text-xs px-2 py-1 rounded-full border border-green-500/40 backdrop-blur-sm">
                  Activa
                </span>
              ) : (
                <span className="bg-red-500/30 text-red-400 text-xs px-2 py-1 rounded-full border border-red-500/40 backdrop-blur-sm">
                  Inactiva
                </span>
              )}
            </div>
          )}
        </motion.div>

        {/* Member Info Overlay - positioned at bottom */}
        <motion.div 
          className="absolute bottom-4 left-5 right-5"
          style={{ transform: 'translateZ(20px)' }}
        >
          <div className="flex items-center justify-between text-white/90">
            <p className="font-bold text-sm uppercase tracking-wider drop-shadow-lg">
              {profile.nombre}
            </p>
            {!isAdmin && (
              <p className="text-xs opacity-80 flex items-center gap-1 drop-shadow-lg">
                <Smartphone className="w-3 h-3" />
                {profile.modelo_celular}
              </p>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Admin Action Button - side on desktop, centered below on mobile - only if canToggleStatus */}
      {isAdmin && onStatusToggle && canToggleStatus && (
        <div className="flex justify-center lg:block">
          <motion.button
            onClick={onStatusToggle}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`mt-4 lg:mt-0 lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:translate-x-[calc(100%+1rem)] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-medium text-sm transition-all whitespace-nowrap ${
              membership.is_active
                ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                : 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
            }`}
          >
            {membership.is_active ? (
              <>
                <XCircle className="w-4 h-4" />
                <span className="lg:hidden xl:inline">Cancelar Membresía</span>
                <span className="hidden lg:inline xl:hidden">Cancelar</span>
              </>
            ) : (
              <>
                <Play className="w-4 h-4" />
                <span className="lg:hidden xl:inline">Reactivar Membresía</span>
                <span className="hidden lg:inline xl:hidden">Reactivar</span>
              </>
            )}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
};

export default MembershipCard;
